import { Close, MoreVert } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Container,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { FeatureCollection } from "@turf/turf";
import { useNotification } from "app/NotificationProvider";
import { HectaresIcon, SwardCompositionIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { FieldEditActionMenu } from "features/fieldDetail";
import AfterFieldCreatedDialog from "features/fieldEdit/AfterFieldCreatedDialog";
import ArableTypeDropdown from "features/fieldEdit/ArableTypeDropdown";
import mixpanel from "mixpanel-browser";
import {
  ArableField,
  ARABLE_TYPE,
  changeFieldVisibility,
  FIELD_TYPE,
  getArableFields,
  getGrainFields,
  selectCurrentFarm,
  setIsEditing,
  setZoomToGeom,
  updateArableField,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

interface Props {
  field: ArableField;
}

interface LocationState {
  previousPath?: string;
  previousSearch?: string;
}

export default function ArableFieldDetails({ field }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const location = useLocation<LocationState>();
  const currentFarm = useAppSelector(selectCurrentFarm);

  const dispatch = useAppDispatch();
  const { notify } = useNotification();

  // Get field lists for navigation logic
  const grainFields = useAppSelector(getGrainFields);
  const arableFields = useAppSelector(getArableFields);

  const fieldGeom = {
    type: "FeatureCollection",
    features: [field.geom],
  } as FeatureCollection;

  useEffect(() => {
    dispatch(setZoomToGeom(fieldGeom));
  }, [field.guid, dispatch]);

  async function handleArableTypeChange(
    value: ARABLE_TYPE | undefined,
    field: ArableField
  ) {
    if (value && field) {
      try {
        await dispatch(
          updateArableField({
            ...field,
            arableType: value,
          })
        ).then(unwrapResult);
        mixpanel.track("Field arable type updated", {
          fieldGuid: field.guid,
          grassType: value,
        });
      } catch (e) {
        console.error("Failed to update grass type", e);
        notify(t("fieldinput.grassType.updateFailed"), "error");
      }
    }
  }

  const handleBack = () => {
    // If we have a previous path, use it
    if (location.state?.previousPath) {
      history.push({
        pathname: location.state.previousPath,
        search: location.state.previousSearch || "",
      });
      return;
    }

    // Determine field type and navigate to appropriate fields list
    const isGrainField = grainFields.some((f) => f?.guid === field.guid);
    if (isGrainField) {
      history.push("/fields-list/grain");
      return;
    }

    const isArableField = arableFields.some((f) => f?.guid === field.guid);
    if (isArableField) {
      history.push("/fields-list/arable");
      return;
    }

    // Fallback to farm view if no previous path
    backToFarm();
  };

  const backToFarm = () => {
    if (currentFarm?.guid) {
      history.push(`/farm/${currentFarm.guid}`);
    }
  };

  const iconSize = "medium";

  return (
    <>
      {/* Desktop header with back button */}
      {!isMobile && (
        <Header>
          <BackButton onClick={handleBack} edge="start" size="large">
            <ArrowBackIcon />
          </BackButton>
          <TitleContainer>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Field
            </Typography>
          </TitleContainer>
        </Header>
      )}

      {/* Mobile header with back button */}
      {isMobile && (
        <Container
          maxWidth="sm"
          sx={{
            pb: 2,
            paddingTop: "calc(env(safe-area-inset-top) + 16px)",
          }}
        >
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.1)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
              {field.name}
            </Typography>
          </Box>
        </Container>
      )}

      {/* New Design - Main Content Card */}
      <Box sx={{ pb: 2, backgroundColor: "#F7FCF1" }}>
        <Box
          sx={{
            backgroundColor: "white",
            px: 3,
          }}
        >
          {/* Header Section */}
          <Box sx={{ pt: 2, pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {field.name}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton onClick={openMenu}>
                  <MoreVert />
                </IconButton>
                {isMobile && (
                  <IconButton
                    size="small"
                    onClick={handleBack}
                    sx={{
                      backgroundColor: "#DBF4C2",
                      color: "white",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <Close fontSize="small" htmlColor="#1C4321" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>

          {/* Field Metrics Section */}
          <Box sx={{ pb: 3 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "text.primary",
                paddingTop: 4,
              }}
            >
              {t("fieldDetail.title")}
            </Typography>

            <Stack spacing={2}>
              {/* Area */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <HectaresIcon fontSize={iconSize} htmlColor="#1C4321" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "black" }}
                    >
                      {field ? `${(field.area / 10000).toFixed(2)} ha` : ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldDetail.area")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Divider after Area */}
              <Divider />

              {/* Arable Type */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SwardCompositionIcon />
                  </Box>
                  <Box>
                    {!field.arableType ? (
                      <ArableTypeDropdown
                        disabled={false}
                        onChange={async (value?: ARABLE_TYPE) => {
                          await handleArableTypeChange(value, field);
                        }}
                        currentArableType={field.arableType}
                        arableTypeInputError={false}
                        label={"fieldinput.arableType.text"}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "black" }}
                      >
                        {t(`fieldinput.arableType.option.${field.arableType}`)}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldinput.arableType.text")}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  sx={{ color: "text.secondary" }}
                  onClick={() => {
                    dispatch(setIsEditing());
                    dispatch(
                      changeFieldVisibility({
                        guid: field.guid,
                        visibility: "none",
                      })
                    );
                    mixpanel.track("FieldEdit successful");
                    history.push(`/edit-field/${field.guid}/enter-details`);
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <FieldEditActionMenu
        anchor={menuAnchor}
        close={closeMenu}
        fieldId={field.guid || ""}
        fieldType={FIELD_TYPE.ARABLE}
      />
      <AfterFieldCreatedDialog />
    </>
  );

  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }
}

const Header = styled.div`
  background-color: white;
  padding: 16px;
  padding-top: calc(env(safe-area-inset-top) + 16px);
  display: flex;
  align-items: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 8px;
`;

const TitleContainer = styled.div`
  flex: 1;
  text-align: center;
  margin: 0 48px;
`;

const FieldEventListContainer = styled(Container)`
  flex-grow: 1;
`;
